import {
    EmployeeGet,
    EmployeeGetExtResponse,
    IBufferedCachingList,
    SubscribeState,
    TimeContractGetResponse,
    Ulid,
} from "@kase/my-marathon-communication";
import { computed, signal } from "@preact/signals";
import { store } from "~/core/store";
import { dateFromApi, upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import {
    Employee,
    EmployeeFeeCode,
    GetHourlyRateParams,
    TimeContract,
} from "./interfaces";
import { getContractKey, isContractKey } from "./utils";

const enum ContractState {
    Started = 1,
    Finished,
}

export const makeEmployee = ({ connection, company }: ApiSliceParams) => {
    const contractsState: Record<string, ContractState> = {};
    const contractsStore: Record<string, TimeContract[]> = {};

    const isContractsLoading = (id: string) => {
        for (const key in contractsState) {
            if (
                isContractKey(id, key) &&
                contractsState[key] === ContractState.Started
            ) {
                return true;
            }
        }

        return false;
    };

    const view = (employee: EmployeeGet) => {
        const id = employee.id.toString();

        const finalEmployee: Employee = {
            ...employee,
            id,
            _id: employee.id,
            feeCodes: signal([]),
            loadingContracts: signal(isContractsLoading(id)),
            loadingActivities: signal(false),
            contracts: signal(contractsStore[id] ?? []),
            contractsFailed: signal(false),
        };

        if (contractsStore[id]) {
            delete contractsStore[id];
        }

        return finalEmployee;
    };

    const {
        items: employees,
        allLoading: loading,
        listen,
        whenLoaded,
        get,
        getOrLoad,
    } = createListController<EmployeeGet, Employee>({
        name: "employee",
        connection,
        list: (con) => con.employee.items({ company: company.value }),
        view,
    });

    const me = computed(
        () =>
            employees.value.find(
                (e) =>
                    e.preferred_username ===
                    connection.value?.user.preferred_username,
            ) ?? null,
    );

    const bindContracts = (
        employeeId: Readonly<Ulid>,
        contracts: TimeContract[],
    ) => {
        const employee = employees.peek().find((e) => e._id.equals(employeeId));

        if (employee) {
            employee.contracts.value = upsert(
                employee.contracts.peek().slice(),
                contracts,
                "id",
            );
            employee.loadingContracts.value = false;
        } else {
            const id = employeeId.toString();

            if (!(id in contractsStore)) {
                contractsStore[id] = [];
            }

            contractsStore[id] = upsert(
                contractsStore[id].slice(),
                contracts,
                "id",
            );
        }
    };

    const loadFinalContracts = async (
        list: IBufferedCachingList<Ulid, TimeContractGetResponse>,
        employeeId: Readonly<Ulid>,
        from: Date | null,
        to: Date | null,
    ) => {
        for await (const event of list) {
            if (
                event.kind === SubscribeState.Add ||
                event.kind === SubscribeState.Update
            ) {
                bindContracts(
                    employeeId,
                    event.values.map((item) => ({
                        ...item,
                        _id: item.id,
                        id: item.id.toString(),
                        from,
                        to,
                    })),
                );
            } else if (event.kind === SubscribeState.Loaded) {
            }
        }
    };

    const loadContract = async (
        employeeId: Readonly<Ulid>,
        num: number,
        year: number,
        from: Date | null,
        to: Date | null,
    ) => {
        const con = connection.value;
        const result: TimeContract[] = [];
        const list = con.time.contract.items({
            company: company.value,
            id: num,
            year,
        });

        for await (const event of list) {
            if (
                event.kind === SubscribeState.Add ||
                event.kind === SubscribeState.Update
            ) {
                bindContracts(
                    employeeId,
                    event.values.map((item) => ({
                        ...item,
                        _id: item.id,
                        id: item.id.toString(),
                        from,
                        to,
                    })),
                );
            } else if (
                event.kind === SubscribeState.LoadedCache ||
                event.kind === SubscribeState.Loaded
            ) {
                break;
            } else if (event.kind === SubscribeState.Error) {
                throw new Error("Contracts loading error (items)");
            }
        }

        loadFinalContracts(list, employeeId, from, to);

        return result;
    };

    const loadContractExt = (
        employeeId: Readonly<Ulid>,
        extension: EmployeeGetExtResponse,
        year: number,
    ) => {
        return extension.calendar_fields
            .filter(
                ({ calendar_from, calendar_to }) =>
                    !calendar_from ||
                    !calendar_to ||
                    (dateFromApi(calendar_from).getFullYear() <= year &&
                        dateFromApi(calendar_to).getFullYear() >= year),
            )
            .map((field) =>
                loadContract(
                    employeeId,
                    field.calendar_num,
                    year,
                    dateFromApi(field.calendar_from),
                    dateFromApi(field.calendar_to),
                ),
            );
    };

    const loadFinalContractExt = async (
        list: IBufferedCachingList<Ulid, EmployeeGetExtResponse>,
        employeeId: Readonly<Ulid>,
        year: number,
    ) => {
        const promises: Promise<unknown>[] = [];

        for await (const event of list) {
            if (
                event.kind === SubscribeState.Add ||
                event.kind === SubscribeState.Update
            ) {
                promises.push(
                    ...event.values.flatMap((extension) =>
                        loadContractExt(employeeId, extension, year),
                    ),
                );
            } else if (event.kind === SubscribeState.Loaded) {
                break;
            }
        }

        await Promise.all(promises);
    };

    const tryLoadContracts = async (
        employeeId: Readonly<Ulid>,
        year: number,
    ) => {
        const con = connection.value;
        const promises: Promise<unknown>[] = [];
        const employeeBefore = employees
            .peek()
            .find((e) => e._id.equals(employeeId));

        if (employeeBefore) {
            employeeBefore.loadingContracts.value = true;
        }

        const list = con.employee.itemsExt({
            company: company.value,
            id: [employeeId],
        });

        for await (const event of list) {
            if (
                event.kind === SubscribeState.Add ||
                event.kind === SubscribeState.Update
            ) {
                promises.push(
                    ...event.values.flatMap((extension) =>
                        loadContractExt(employeeId, extension, year),
                    ),
                );
            } else if (
                event.kind === SubscribeState.LoadedCache ||
                event.kind === SubscribeState.Loaded
            ) {
                break;
            }
        }

        loadFinalContractExt(list, employeeId, year);

        await Promise.all(promises);
    };

    const loadContracts = async (employeeId: Readonly<Ulid>, year: number) => {
        const key = getContractKey(employeeId, year);
        if (contractsState[key]) {
            return;
        }

        contractsState[key] = ContractState.Started;

        let tryCount = 0;
        let success = false;

        do {
            try {
                await tryLoadContracts(employeeId, year);
                success = true;
            } catch (error: unknown) {
                console.log(
                    "Error occurred while trying to load contracts",
                    error,
                );
            }

            ++tryCount;
        } while (tryCount < 3 && !success);

        contractsState[key] = ContractState.Finished;

        const employee = employees.peek().find((e) => e._id.equals(employeeId));

        if (!employee) {
            return;
        }

        if (success) {
            employee.loadingContracts.value = false;
        } else {
            employee.contractsFailed.value = true;
        }
    };

    const loadFeeCodes = async (employee: Employee) => {
        const con = connection.value;
        const { feeCode } = store.api;
        if (employee.feeCodes.peek().length) {
            return;
        }

        employee.loadingActivities.value = true;

        const result = con.employee.feecode.list({
            company: company.value,
            employee: [employee._id],
        });
        const employeeActivitiesResult: Array<EmployeeFeeCode> = [];

        for await (const ids of result) {
            const codes = con.employee.feecode.get({
                company: company.value,
                id: ids,
            });

            for await (const code of codes) {
                // @TODO: check if getAsync is needed here
                const finalCode = feeCode.get(code.fee_code.toString());

                if (finalCode) {
                    employeeActivitiesResult.push({
                        ...finalCode,
                        employeeCodeId: code.id,
                    });
                }
            }
        }

        employee.feeCodes.value = upsert(
            employee.feeCodes.peek().slice(),
            employeeActivitiesResult,
            "id",
        );

        employee.loadingActivities.value = false;
    };

    // @TODO: temporary solution
    const openEmployees = async () => {
        if (employees.peek()[0]?.feeCodes.peek().length) {
            return;
        }

        // @TODO: remove this hack when batch loading is working properly
        const last = employees.peek().at(-1);

        if (last) {
            last.loadingActivities.value = true;
        }

        for (const employee of employees.peek()) {
            await loadFeeCodes(employee);
        }
    };

    const rate = async (params: GetHourlyRateParams) => {
        const con = connection.value;
        const result = await con.employee.hourlyRate({
            company: company.value,
            ...params,
        });

        return result.rate?.toNumber();
    };

    return {
        me,
        employees,
        loading,
        listen,
        whenLoaded,
        rate,
        openEmployees,
        get,
        getOrLoad,
        loadContracts,
    };
};
